import React, { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'
import moment from 'moment'

import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'

import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'components/Luxkit/Typography/Heading'
import Pane from 'components/Common/Pane'
import TextButton from 'components/Luxkit/Button/TextButton'
import useCheapestOfTourV2Offer from 'hooks/TourV2/useCheapestOfTourV2Offer'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import Pill from 'components/Luxkit/Pill'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { useTourV2PricePerDay } from 'hooks/TourV2/useTourV2PricePerDay'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import isTourV2DepositAllowed from 'lib/TourV2/isTourV2DepositAllowed'
import CurrencyContext from 'contexts/currencyContext'
import { getDepositAmountPercentage } from 'lib/depositsUtils'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { useAppSelector } from 'hooks/reduxHooks'
import LineUsersAltIcon from 'components/Luxkit/Icons/line/LineUsersAltIcon'
import PromotionLabel from 'components/Luxkit/Label/PromotionLabel'
import TourV2ItineraryLocationList from 'components/OfferPage/TourV2/TourV2ItineraryLocationList'
import config from 'constants/config'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import TourV2AllInclusionsModal from './TourV2AllInclusionsModal'
import ModalContext from 'contexts/ModalContext'
import TourV2ItemInclusions from 'components/OfferPage/TourV2/TourV2ItemInclusions'

const Root = styled(Pane)`
  align-items: stretch;
  border: 1px solid ${({ theme }) => theme.palette.neutral.six};
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.palette.neutral.six};
  display: grid;
  grid-row-gap: ${rem(12)};
  grid-template-areas:
    "details details"
    "pricing buttons";
  grid-template-columns: 1fr min-content;
  padding: ${rem(20)};
  position: relative;
  transition: .2s ease background, .2s ease box-shadow, .2s ease border;

  &.is-selected {
    border: 1px solid ${({ theme }) => theme.palette.neutral.two};
  }

  &:not(.is-selected):hover {
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.palette.neutral.six},
      ${({ theme }) => theme.shadow.bottom.small};
  }

  ${mediaQueryUp.md} {
    grid-row-gap: ${rem(16)};
    grid-template-areas:
      "details details"
      "pricing buttons";
    padding: ${rem(20)};
    width: ${rem(460)};
    height: 100%;
  }
  &.vertical-view {
    height: 100%;
    min-width: ${rem(350)};
  }
  &.horizontal-view {
    border-radius: ${props => props.theme.borderRadius.large};
    align-items: center;
    grid-row-gap: ${rem(16)};
    padding: ${rem(24)};

    ${mediaQueryUp.md} {
      grid-column-gap: ${rem(20)};
      grid-row-gap: unset;
      grid-template-areas: "details pricing buttons";
      grid-template-columns: 1fr ${rem(180)} ${rem(150)};
      padding: ${rem(24)} ${rem(32)};
      width: initial;
      height: initial;
    }
  }
`

const DetailsContainer = styled.div`
  flex-grow: 1;
  grid-area: details;
`

const PricingContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: pricing;
  justify-content: flex-end;

  &.horizontal-view {
    justify-content: center;
  }
`

const ButtonsContainer = styled.div`
  align-items: end;
  align-self: flex-end;
  display: flex;
  flex-basis: auto;
  flex-direction: row;
  grid-area: buttons;
  justify-content: end;
  width: 100%

  ${mediaQueryUp.md} {
    align-items: stretch;
    align-self: flex-end;
    flex-direction: column;
    justify-self: end;
    width: 100%;
  }

  &.horizontal-view {
    ${mediaQueryUp.md} {
      align-self: center;
    }
  }
`

const Headsubtitle = styled(BodyText)`
  margin: ${rem(12)} 0;
`

const Subtitle = styled(BodyText)`
  margin-top: ${rem(4)};
`

const CaptionContainer = styled.div`
  align-items: center;
  display: flex;
  margin-top: ${rem(8)};

  > * + *::before {
    content: '·';
    margin: 0 ${rem(4)};
    speak-as: bullets;
    speak: none;
  }

  &.horizontal-view {
    margin-top: ${rem(8)};
  }
`

const PricingCaptionContainer = styled(Caption)`
  align-items: center;
  display: flex;

  > * + *::before {
    color: ${({ theme }) => theme.palette.neutral.one};
    content: '·';
    margin: 0 ${rem(4)};
    speak-as: bullets;
    speak: none;
  }
`

const ValuedAtCaption = styled(Caption)`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
`

const SelectedButton = styled(TextButton)`
  cursor: unset;
`

const InclusionContainer = styled.div`
  margin-top: ${rem(12)};
  gap: ${rem(4)};
`

const ListInclusion = styled.div`
  margin-top: ${rem(12)};
  > div {
    padding: ${rem(4)} 0;
  }
`
const LabelsContainer = styled.div`
  display: flex;
  margin-top: ${rem(12)};
`

interface MappedStateProps {
  toursPricePerDayABTestVariation: string
}

interface Props {
  earliestDeparture: Tours.V2OfferDeparture
  isSelected: boolean
  latestDeparture: Tours.V2OfferDeparture
  offer: Tours.V2Offer
  variation: Tours.V2OfferVariation
  position: number
  onVariationSelect: (variationId: Tours.V2OfferVariation['id']) => void
  isPackageUpgrade: boolean
}

function TourV2VariationCard(props: Props & MappedStateProps) {
  const {
    earliestDeparture,
    isSelected,
    toursPricePerDayABTestVariation,
    latestDeparture,
    offer,
    variation,
    onVariationSelect,
    position,
  } = props

  const {
    durationInDaysCount,
    countriesCount,
    placesCount,
    hasTheSameStartAndEndLocation,
    startLocation,
    endLocation,
    inclusionItems,
    itineraryDestinations,
    isBase,
    hasExtension,
    availablePackageUpgrades,
  } = useTourV2VariationDetails(variation)

  const showModal = useContext(ModalContext)
  const {
    cheapestPurchasableOption,
  } = useCheapestOfTourV2Offer(offer, variation.id)

  const purchasableOptionDetails = useTourV2PurchasableOptionDetails(cheapestPurchasableOption)

  const {
    isDefaultPriceView,
    isVariationLeading,
    pricePerDay,
  } = useTourV2PricePerDay(offer, cheapestPurchasableOption, variation, toursPricePerDayABTestVariation)

  const departureRangeText = useMemo(
    () => {
      if (moment(earliestDeparture.startDate).isSame(moment(latestDeparture.startDate))) {
        return `On ${moment(earliestDeparture.startDate).format(DMY_CASUAL_FORMAT)}`
      }
      return `From ${moment(earliestDeparture.startDate).format(DMY_CASUAL_FORMAT)} to ${moment(latestDeparture.startDate).format(DMY_CASUAL_FORMAT)}`
    },
    [earliestDeparture, latestDeparture],
  )

  const currentCurrency = useContext(CurrencyContext)
  const defaultDepositAmountPercentage = useAppSelector(getDefaultDepositAmountPercentage)
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const isDepositAllowed = isTourV2DepositAllowed(latestDeparture.startDate, purchasableOptionDetails?.pricePerPerson ?? 0, currentCurrency, offer.depositType)

  const handleTourOptionSelect = useCallback(() => {
    onVariationSelect(variation.id)
  }, [onVariationSelect, variation])

  const [showInclusionInline, setShowInclusionInline] = useState(false)
  const isPackageUpgradeEnabled = config.TOUR_V2_PACKAGE_UPGRADE
  const { inclusionsToBeDisplayed, inclusionsLeft } = useMemo<{
    inclusionsToBeDisplayed: Tours.InclusionItem[]
    inclusionsLeft: Tours.InclusionItem[]
  }>(() => {
    const items = isBase ? inclusionItems : inclusionItems.filter(item => item.isHighlight)
    const inclusionsLimitToDisplay = 5
    return {
      inclusionsToBeDisplayed: items.slice(0, inclusionsLimitToDisplay),
      inclusionsLeft: items.slice(inclusionsLimitToDisplay),
    }
  }, [inclusionItems, isBase])

  const showMoreInclusion = useCallback(() => {
    if (inclusionsLeft.length <= 2) {
      setShowInclusionInline(true)
    } else {
      showModal(<TourV2AllInclusionsModal inclusionItems={inclusionItems} variation={variation} />)
    }
  }, [showModal, variation, inclusionItems, inclusionsLeft])

  return <div><Root type="clean" className={cn({ 'is-selected': isSelected, 'horizontal-view': !isPackageUpgradeEnabled, 'vertical-view': isPackageUpgradeEnabled })}>
    {isPackageUpgradeEnabled && <>
      <DetailsContainer>
        {hasExtension && <StructureLabel variant="standard">{isBase ? 'Base' : 'Extension'}</StructureLabel>}
        {!hasExtension && <StructureLabel variant="standard">Option {position}</StructureLabel>}
        <Headsubtitle variant="medium">
          {hasTheSameStartAndEndLocation && <span>Starts and ends in <b>{endLocation}</b></span>}
          {!hasTheSameStartAndEndLocation && <span>Starts in <b>{startLocation}</b>. Ends in <b>{endLocation}</b></span>}
        </Headsubtitle>
        <Heading variant="heading5" lineClamp={3}>{variation.name}</Heading>

        <CaptionContainer>
          <BodyText variant="medium">{durationInDaysCount}</BodyText>
          {placesCount && <BodyText variant="medium">{placesCount}</BodyText>}
          {countriesCount && <BodyText variant="medium">{countriesCount}</BodyText>}
        </CaptionContainer>

        <InclusionContainer>
          <BodyText weight="bold" variant="large">{!hasExtension || isBase ? 'Highlights include:' : 'Includes everything in Base plus:'}</BodyText>
          <ListInclusion>
            <TourV2ItineraryLocationList
              prefix={!hasExtension || isBase ? 'Destinations: ' : 'Additional destinations: '}
              locationsVisited={itineraryDestinations}
            />
            <TourV2ItemInclusions inclusions={inclusionsToBeDisplayed} />
            {!showInclusionInline && inclusionsLeft.length > 0 && <TextButton kind="tertiary" onClick={showMoreInclusion}>
              +{inclusionsLeft.length} more inclusions
            </TextButton>}
            {showInclusionInline && <TourV2ItemInclusions inclusions={inclusionsLeft} />}
          </ListInclusion>
        </InclusionContainer>
        <LabelsContainer>
          {(availablePackageUpgrades?.length ?? 0) > 0 && <PromotionLabel variant="default">Multiple packages available</PromotionLabel>}
          {variation.isPrivateRequest && <PromotionLabel variant="default" icon={<LineUsersAltIcon />}>Private tours available</PromotionLabel>}
        </LabelsContainer>
      </DetailsContainer>

      <PricingContainer>
        {!!purchasableOptionDetails && <>
          <Caption variant="medium" colour="neutral-three">From</Caption>
          <Heading variant="heading4" as="span">
            <FormatCurrency value={purchasableOptionDetails.pricePerPerson} format="roundedDollar" />
            <BodyText variant="medium" as="span">&nbsp;/person</BodyText>
          </Heading>
          <Caption variant="medium" colour="neutral-three">Includes taxes & fees</Caption>
          <Caption variant="medium" colour="neutral-three">{purchasableOptionDetails.roomTypeName}</Caption>
          { !!purchasableOptionDetails.valuedAt &&
            <ValuedAtCaption variant="medium" weight="bold">
              <span>
                Valued up to <FormatCurrency value={purchasableOptionDetails.valuedAt.price} format="roundedDollar" />
              </span>
              <Pill kind="primary" variant="success">-{purchasableOptionDetails.valuedAt.savingPercentage}%</Pill>
            </ValuedAtCaption>}
        </>}
        {!purchasableOptionDetails && <Heading variant="heading5">Sold Out</Heading>}
      </PricingContainer>
    </>}

    {!isPackageUpgradeEnabled && <>
      <DetailsContainer>
        <Heading variant="heading6">{variation.name}</Heading>
        <Subtitle variant="medium">{departureRangeText}</Subtitle>

        <CaptionContainer>
          <BodyText variant="medium" weight="bold">{durationInDaysCount}</BodyText>
          {placesCount && <BodyText variant="medium" weight="bold">{placesCount}</BodyText>}
          {countriesCount && <BodyText variant="medium" weight="bold">{countriesCount}</BodyText>}
        </CaptionContainer>
      </DetailsContainer>

      <PricingContainer className="horizontal-view">
        {!!purchasableOptionDetails && <>
          {!isDefaultPriceView && isDepositAllowed &&
            <SecureWithDepositTagAndModal
              depositAmount={offer.depositAmount}
              depositType={offer.depositType}
              offerType={offer.type}
              depositAmountPercentage={depositAmountPercentage}
            />}
          <Caption variant="medium" colour="neutral-three">From</Caption>
          { isVariationLeading &&
            <div>
              <Heading variant="heading3" as="span">
                <FormatCurrency value={pricePerDay} format="roundedDollar" />
              </Heading>
              <BodyText variant="medium" as="span">&nbsp;/day</BodyText>
            </div>}
          { isDefaultPriceView &&
            <div>
              <Heading variant="heading3" as="span">
                <FormatCurrency value={purchasableOptionDetails.pricePerPerson} format="roundedDollar" />
              </Heading>
              <BodyText variant="medium" as="span">&nbsp;/person</BodyText>
            </div>}
          <PricingCaptionContainer variant="medium" colour="neutral-three">
            { isVariationLeading && <span><FormatCurrency value={purchasableOptionDetails.pricePerPerson} format="roundedDollar" />/person</span>}
            <span>{purchasableOptionDetails.roomTypeName}</span>
          </PricingCaptionContainer>
          { isDefaultPriceView && !!purchasableOptionDetails.valuedAt && <ValuedAtCaption variant="medium" weight="bold">
            <span>
              Valued up to <FormatCurrency value={purchasableOptionDetails.valuedAt.price} format="roundedDollar" />
            </span>
            <Pill kind="primary" variant="success">-{purchasableOptionDetails.valuedAt.savingPercentage}%</Pill>
          </ValuedAtCaption>}
          { isVariationLeading && <Caption variant="medium" colour="neutral-three">Includes taxes & fees</Caption>}
        </>}
        {!purchasableOptionDetails && <Heading variant="heading5">Sold Out</Heading>}
      </PricingContainer>
    </>}

    <ButtonsContainer className={cn({ 'horizontal-view': !isPackageUpgradeEnabled })}>
      {isSelected && <SelectedButton kind={isPackageUpgradeEnabled ? 'primary' : 'tertiary'} variant="dark" nonInteractable tabIndex={-1}>
        Selected
      </SelectedButton>}
      {!isSelected && <TextButton kind="secondary" onClick={handleTourOptionSelect}>
        View tour
      </TextButton>}
    </ButtonsContainer>
  </Root></div>
}

export default withOfferPageState<MappedStateProps>((offerPageState) => ({
  toursPricePerDayABTestVariation: offerPageState.toursPricePerDayABTestVariation,
}))(TourV2VariationCard)
